import {
	getAnalytics,
	isSupported,
	logEvent,
	setUserProperties as firebaseSetUserProperties,
} from 'firebase/analytics';
import { getApp, getApps } from 'firebase/app';

import { getLogger } from '@src/lib/logging/logger';

const logger = getLogger('client');

/**
 * Firebase Analytics event names used by this app.
 *
 * These land in the Google Analytics property linked to the Firebase project
 * and are what Firebase A/B Testing experiments can select as goal metrics.
 * Keep the names snake_case and under 40 characters (GA4 limit).
 *
 * Do NOT suffix event names with the platform — Firebase automatically tags
 * every event with a built-in `platform` dimension, so mobile and web can
 * share the same event name and still be sliced per-platform in reports.
 */
export const FIREBASE_ANALYTICS_EVENTS = {
	appointmentCompleted: 'appointment_completed',
	documentUpload: 'document_upload',
	documentUploadFailed: 'document_upload_failed',
} as const;

export type FirebaseEventParams = Record<string, string | number>;

/**
 * Parameters for the `appointment_completed` conversion event.
 *
 * Naming intentionally mirrors the existing GTM `dataLayer` keys so the two
 * streams can be reconciled.
 *
 * Deliberately excluded:
 * - `client_id` / `appointment_id`: identifiers must not be sent to GA4.
 * - `appointment_date` / `appointment_time`: unbounded cardinality, and GA4
 *   allows only 50 custom dimensions per property.
 */
export type AppointmentCompletedParams = {
	hospital_id?: string;
	hospital_name?: string;
	visitor_type: 'new' | 'existing';
	reason_for_visit?: string;
	scheduling?: string;
};

export const getAnalyticsInstance = async () => {
	if (globalThis.window === undefined) return null;
	if (!getApps().length) return null;
	if (!(await isSupported())) return null;
	return getAnalytics(getApp());
};

/**
 * Log an event to Firebase Analytics.
 *
 * Analytics must never break the booking flow, so every failure path here is
 * swallowed and logged. `isSupported()` is checked because `getAnalytics()`
 * throws in environments where cookies or IndexedDB are unavailable (blocked
 * third-party storage, some in-app webviews), which is a non-trivial share of
 * real traffic.
 */
export const logFirebaseEvent = async (name: string, params: FirebaseEventParams = {}): Promise<void> => {
	try {
		const analytics = await getAnalyticsInstance();
		if (!analytics) {
			logger.warn({ event: name }, 'FIREBASE_ANALYTICS_UNAVAILABLE');
			return;
		}
		logEvent(analytics, name, params);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown logEvent error';
		logger.warn({ error, message, event: name }, 'FIREBASE_ANALYTICS_LOG_EVENT_FAILED');
	}
};

/**
 * Fire the `appointment_completed` conversion event.
 *
 * Call this once, immediately after a confirmed successful appointment
 * creation — not on the confirmation page, which re-mounts on refresh and
 * back navigation.
 */
export const logAppointmentCompleted = (params: AppointmentCompletedParams): void => {
	void logFirebaseEvent(FIREBASE_ANALYTICS_EVENTS.appointmentCompleted, params);
};

/** Parameters for the `document_upload` conversion event. */
export type DocumentUploadParams = {
	file_type: string;
	file_size_kb: number;
	upload_context: string;
};

/** Parameters for the `document_upload_failed` event. */
export type DocumentUploadFailedParams = {
	file_type: string;
	failure_reason: string;
};

/** Fire once per file, immediately after a confirmed successful document upload. */
export const logDocumentUpload = (params: DocumentUploadParams): void => {
	void logFirebaseEvent(FIREBASE_ANALYTICS_EVENTS.documentUpload, params);
};

/** Fire once per file that fails to upload. */
export const logDocumentUploadFailed = (params: DocumentUploadFailedParams): void => {
	void logFirebaseEvent(FIREBASE_ANALYTICS_EVENTS.documentUploadFailed, params);
};

/**
 * User properties automatically applied to every subsequent event.
 * Used to slice A/B experiment results in the Firebase Console.
 */
export type ExperimentUserProperties = {
	nc_phone_verification_variant: string;
};

/**
 * Set A/B experiment variant user properties.
 *
 * After this call, every subsequent event Firebase records (custom and
 * built-in) is automatically tagged with these properties, so any Firebase
 * report or A/B test can be filtered/segmented by variant without
 * re-passing the values on individual events.
 *
 * Safe to call repeatedly — Firebase merges properties.
 */
export const setExperimentUserProperties = async (properties: ExperimentUserProperties): Promise<void> => {
	try {
		const analytics = await getAnalyticsInstance();
		if (!analytics) return;
		firebaseSetUserProperties(analytics, properties);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown setUserProperties error';
		logger.warn({ error, message }, 'FIREBASE_ANALYTICS_SET_USER_PROPERTIES_FAILED');
	}
};
